import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { Image, MessageSquare, CheckCircle, XCircle, Clock, Star, Bell, Sparkles } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { SocialMediaAssetGenerator } from "@/components/social-media/SocialMediaAssetGenerator";

export const ContentApprovalDashboard = () => {
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectNotes, setRejectNotes] = useState("");
  const [itemType, setItemType] = useState<'ba' | 'testimonial'>('ba');

  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch all pending before/after submissions
  const { data: baSubmissions = [], isLoading: baLoading } = useQuery({
    queryKey: ['admin-ba-submissions'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('provider_before_after_submissions')
        .select(`
          *,
          locations (name, city, state)
        `)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data || [];
    },
  });

  // Fetch all pending testimonial submissions
  const { data: testimonialSubmissions = [], isLoading: testLoading } = useQuery({
    queryKey: ['admin-testimonial-submissions'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('provider_testimonial_submissions')
        .select(`
          *,
          locations (name, city, state)
        `)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data || [];
    },
  });

  const pendingBA = baSubmissions.filter((s: any) => s.status === 'pending');
  const pendingTest = testimonialSubmissions.filter((s: any) => s.status === 'pending');

  const approveBAMutation = useMutation({
    mutationFn: async (id: string) => {
      const { data: { user } } = await supabase.auth.getUser();
      
      const { error } = await supabase
        .from('provider_before_after_submissions')
        .update({
          status: 'approved',
          approved_at: new Date().toISOString(),
          approved_by: user?.id,
        })
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-ba-submissions'] });
      toast({ title: "Approved", description: "Before/after photos have been approved and are now visible." });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to approve submission.", variant: "destructive" });
    },
  });

  const rejectBAMutation = useMutation({
    mutationFn: async ({ id, notes }: { id: string; notes: string }) => {
      const { error } = await supabase
        .from('provider_before_after_submissions')
        .update({
          status: 'rejected',
          admin_notes: notes,
        })
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-ba-submissions'] });
      setRejectDialogOpen(false);
      setRejectNotes("");
      toast({ title: "Rejected", description: "Submission has been rejected." });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to reject submission.", variant: "destructive" });
    },
  });

  const approveTestMutation = useMutation({
    mutationFn: async (id: string) => {
      const { data: { user } } = await supabase.auth.getUser();
      
      const { error } = await supabase
        .from('provider_testimonial_submissions')
        .update({
          status: 'approved',
          approved_at: new Date().toISOString(),
          approved_by: user?.id,
        })
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-testimonial-submissions'] });
      toast({ title: "Approved", description: "Testimonial has been approved and is now visible." });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to approve testimonial.", variant: "destructive" });
    },
  });

  const rejectTestMutation = useMutation({
    mutationFn: async ({ id, notes }: { id: string; notes: string }) => {
      const { error } = await supabase
        .from('provider_testimonial_submissions')
        .update({
          status: 'rejected',
          admin_notes: notes,
        })
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-testimonial-submissions'] });
      setRejectDialogOpen(false);
      setRejectNotes("");
      toast({ title: "Rejected", description: "Testimonial has been rejected." });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to reject testimonial.", variant: "destructive" });
    },
  });

  const handleReject = () => {
    if (itemType === 'ba') {
      rejectBAMutation.mutate({ id: selectedItem.id, notes: rejectNotes });
    } else {
      rejectTestMutation.mutate({ id: selectedItem.id, notes: rejectNotes });
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'approved':
        return <Badge className="bg-green-500"><CheckCircle className="w-3 h-3 mr-1" />Approved</Badge>;
      case 'rejected':
        return <Badge variant="destructive"><XCircle className="w-3 h-3 mr-1" />Rejected</Badge>;
      default:
        return <Badge variant="secondary"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
    }
  };

  const totalPending = pendingBA.length + pendingTest.length;

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Image className="w-5 h-5" />
            Content Approvals
            {totalPending > 0 && (
              <Badge variant="destructive" className="ml-2">
                <Bell className="w-3 h-3 mr-1" />
                {totalPending} pending
              </Badge>
            )}
          </CardTitle>
          <CardDescription>
            Review and approve provider-submitted before/after photos and testimonials
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="before-after" className="space-y-6">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="before-after" className="relative">
                <Image className="w-4 h-4 mr-2" />
                <span className="hidden sm:inline">Before/After</span>
                <span className="sm:hidden">B/A</span>
                {pendingBA.length > 0 && (
                  <Badge variant="destructive" className="ml-2 h-5 w-5 p-0 flex items-center justify-center text-xs">
                    {pendingBA.length}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="testimonials" className="relative">
                <MessageSquare className="w-4 h-4 mr-2" />
                <span className="hidden sm:inline">Testimonials</span>
                <span className="sm:hidden">Reviews</span>
                {pendingTest.length > 0 && (
                  <Badge variant="destructive" className="ml-2 h-5 w-5 p-0 flex items-center justify-center text-xs">
                    {pendingTest.length}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="social-generator" className="relative">
                <Sparkles className="w-4 h-4 mr-2" />
                <span className="hidden sm:inline">Create Posts</span>
                <span className="sm:hidden">Create</span>
                <Badge variant="secondary" className="ml-2 text-xs">New</Badge>
              </TabsTrigger>
            </TabsList>

            <TabsContent value="before-after" className="space-y-4">
              {baLoading ? (
                <p className="text-muted-foreground text-center py-8">Loading...</p>
              ) : baSubmissions.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">No before/after submissions yet.</p>
              ) : (
                <div className="space-y-4">
                  {baSubmissions.map((submission: any) => (
                    <div 
                      key={submission.id} 
                      className={`border rounded-lg p-4 ${submission.status === 'pending' ? 'border-yellow-500/50 bg-yellow-50/50 dark:bg-yellow-950/20' : ''}`}
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <p className="font-medium">{submission.locations?.name}</p>
                          <p className="text-sm text-muted-foreground">
                            {submission.locations?.city}, {submission.locations?.state}
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">
                            Submitted: {new Date(submission.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        {getStatusBadge(submission.status)}
                      </div>

                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">Before</p>
                          <img 
                            src={submission.before_image_url} 
                            alt="Before" 
                            className="w-full aspect-square object-cover rounded-lg border"
                          />
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">After</p>
                          <img 
                            src={submission.after_image_url} 
                            alt="After" 
                            className="w-full aspect-square object-cover rounded-lg border"
                          />
                        </div>
                      </div>

                      {submission.treatment_type && (
                        <p className="text-sm mb-2"><strong>Treatment:</strong> {submission.treatment_type}</p>
                      )}
                      {submission.description && (
                        <p className="text-sm text-muted-foreground mb-4">{submission.description}</p>
                      )}

                      {submission.status === 'pending' && (
                        <div className="flex gap-2">
                          <Button 
                            size="sm" 
                            onClick={() => approveBAMutation.mutate(submission.id)}
                            disabled={approveBAMutation.isPending}
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Approve
                          </Button>
                          <Button 
                            size="sm" 
                            variant="destructive"
                            onClick={() => {
                              setSelectedItem(submission);
                              setItemType('ba');
                              setRejectDialogOpen(true);
                            }}
                          >
                            <XCircle className="w-4 h-4 mr-1" />
                            Reject
                          </Button>
                        </div>
                      )}

                      {submission.admin_notes && (
                        <p className="text-sm text-muted-foreground mt-2 italic">
                          Admin note: {submission.admin_notes}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="testimonials" className="space-y-4">
              {testLoading ? (
                <p className="text-muted-foreground text-center py-8">Loading...</p>
              ) : testimonialSubmissions.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">No testimonial submissions yet.</p>
              ) : (
                <div className="space-y-4">
                  {testimonialSubmissions.map((submission: any) => (
                    <div 
                      key={submission.id} 
                      className={`border rounded-lg p-4 ${submission.status === 'pending' ? 'border-yellow-500/50 bg-yellow-50/50 dark:bg-yellow-950/20' : ''}`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <p className="font-medium">{submission.locations?.name}</p>
                          <p className="text-sm text-muted-foreground">
                            {submission.locations?.city}, {submission.locations?.state}
                          </p>
                        </div>
                        {getStatusBadge(submission.status)}
                      </div>

                      <div className="flex items-center gap-2 mb-2">
                        <span className="font-medium">{submission.client_name}</span>
                        <div className="flex items-center">
                          {Array.from({ length: submission.rating }).map((_, i) => (
                            <Star key={i} className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                          ))}
                        </div>
                      </div>

                      <p className="text-muted-foreground mb-2">{submission.testimonial_text}</p>

                      {submission.treatment_type && (
                        <Badge variant="outline" className="mb-4">{submission.treatment_type}</Badge>
                      )}

                      {submission.status === 'pending' && (
                        <div className="flex gap-2 mt-4">
                          <Button 
                            size="sm" 
                            onClick={() => approveTestMutation.mutate(submission.id)}
                            disabled={approveTestMutation.isPending}
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Approve
                          </Button>
                          <Button 
                            size="sm" 
                            variant="destructive"
                            onClick={() => {
                              setSelectedItem(submission);
                              setItemType('testimonial');
                              setRejectDialogOpen(true);
                            }}
                          >
                            <XCircle className="w-4 h-4 mr-1" />
                            Reject
                          </Button>
                        </div>
                      )}

                      {submission.admin_notes && (
                        <p className="text-sm text-muted-foreground mt-2 italic">
                          Admin note: {submission.admin_notes}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="social-generator">
              <SocialMediaAssetGenerator isAdmin={true} />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Submission</DialogTitle>
            <DialogDescription>
              Provide a reason for rejection. This will be visible to the provider.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            placeholder="Reason for rejection..."
            value={rejectNotes}
            onChange={(e) => setRejectNotes(e.target.value)}
            rows={3}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleReject}
              disabled={rejectBAMutation.isPending || rejectTestMutation.isPending}
            >
              Reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
